import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Modal } from '@spirit-testing/component-library';
import { ProjectIntegrationService } from '../../../services/project-integration.service';
import { JiraIssue, ProjectIntegration, ProviderType } from '../../../models/project-integration.model';
import { providerColor as providerColorMeta, providerLabel as providerLabelMeta } from '../../../shared/provider-meta';
import { ProviderBadgeComponent } from '../../../components/provider-badge/provider-badge.component';
import { isImportableIssueType } from '../../../shared/external-issue-type';
import { boardHost } from '../../../shared/issue-links';
import { mapTrackerError, TRACKER_ERROR_KEYS, TrackerError } from '../../../shared/tracker-error';

// Provider whose issues can back requirements. GitHub is a repo provider, not an
// issue tracker, and is deliberately excluded from the picker.
const REQUIREMENTS_PROVIDERS: ReadonlySet<ProviderType> = new Set<ProviderType>(['JIRA', 'REDMINE']);

const ISSUE_TYPE_PATTERNS: { keywords: string[]; cls: string }[] = [
  { keywords: ['story', 'user story', 'geschichte'], cls: 'story' },
  { keywords: ['bug', 'fehler'], cls: 'bug' },
  { keywords: ['epic'], cls: 'epic' },
  { keywords: ['sub-task', 'subtask', 'task', 'aufgabe'], cls: 'task' },
];

/** Emitted when the user links an issue — carries the provider it was picked from. */
export interface IssuePicked {
  issue: JiraIssue;
  providerType: ProviderType | null;
}

@Component({
  selector: 'app-issue-picker-modal',
  imports: [Modal, TranslatePipe, RouterLink, MatIcon, MatProgressSpinner, ProviderBadgeComponent],
  templateUrl: './issue-picker-modal.component.html',
  styleUrl: './issue-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssuePickerModalComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly integrationService = inject(ProjectIntegrationService);
  private hasLoaded = false;

  readonly visible = model(false);
  readonly projectId = input.required<string>();
  readonly issueSelected = output<IssuePicked>();

  readonly loading = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly trackerError = signal<TrackerError | null>(null);
  readonly trackerErrorKeys = TRACKER_ERROR_KEYS;
  readonly search = signal('');
  readonly selectedIssue = signal<JiraIssue | null>(null);
  readonly manualId = signal('');
  /** All active Jira/Redmine integrations of the project (>1 → provider switch shown). */
  readonly providers = signal<ProjectIntegration[]>([]);
  /** Currently selected provider integration whose issues are listed. */
  readonly integration = signal<ProjectIntegration | null>(null);

  private readonly issues = signal<JiraIssue[]>([]);

  readonly hasProviderChoice = computed(() => this.providers().length > 1);

  /**
   * `projectKey` ist seit NEX-969 ein internes Kennzeichen, kein Projektbezug —
   * deshalb heisst es hier "Kürzel" (Redmine) bzw. "Projekt-Key" (Jira) statt
   * "Projekt".
   */
  readonly aliasLabelKey = computed(() =>
    this.integration()?.providerType === 'REDMINE'
      ? 'requirements.issuePicker.aliasLabel.redmine'
      : 'requirements.issuePicker.aliasLabel.jira',
  );

  readonly boardHost = computed(() => boardHost(this.integration()?.boardUrl));

  // Non-importable types (e.g. Redmine „Testfall") are hidden so they can't be picked.
  readonly importable = computed(() => this.issues().filter((i) => isImportableIssueType(i.issueTypeName)));

  readonly filtered = computed(() => {
    const term = this.search().toLowerCase().trim();
    if (!term) return this.importable();
    return this.importable().filter(
      (i) => i.key.toLowerCase().includes(term) || (i.summary ?? '').toLowerCase().includes(term),
    );
  });

  readonly canConfirm = computed(() => this.selectedIssue() !== null || this.manualId().trim().length > 0);

  @ViewChild('content', { static: true }) contentTemplate!: TemplateRef<unknown>;

  constructor() {
    effect(() => {
      if (this.visible() && !this.hasLoaded) {
        this.hasLoaded = true;
        this.loadProviders();
      }
    });
  }

  providerLabel(providerType: ProviderType): string {
    return providerLabelMeta(providerType);
  }

  providerColor(providerType: ProviderType): string {
    return providerColorMeta(providerType);
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  onManualIdInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.manualId.set(value);
    if (value.trim()) {
      this.selectedIssue.set(null);
    }
  }

  onSelectRow(issue: JiraIssue): void {
    this.selectedIssue.set(issue);
    this.manualId.set('');
  }

  /** Switches the picker to another connected provider and reloads its issues. */
  selectProvider(provider: ProjectIntegration): void {
    if (this.integration()?.id === provider.id) return;
    this.integration.set(provider);
    this.selectedIssue.set(null);
    this.manualId.set('');
    this.search.set('');
    this.issues.set([]);
    this.loadIssuesForCurrent();
  }

  onConfirm(): void {
    const providerType = this.integration()?.providerType ?? null;
    const selected = this.selectedIssue();
    if (selected) {
      this.issueSelected.emit({ issue: selected, providerType });
      this.reset();
      this.visible.set(false);
      return;
    }
    // Manual entry: upper-casing normalizes a typed Jira key (nex-1 → NEX-1) and is
    // a no-op for a numeric Redmine id.
    const id = this.manualId().trim().toUpperCase();
    if (id) {
      const found = this.issues().find((i) => i.key.toUpperCase() === id);
      this.issueSelected.emit({
        providerType,
        issue: found ?? {
          id: id,
          key: id,
          summary: null,
          statusName: '',
          statusCategory: '',
          priorityName: '',
          priorityIconUrl: '',
          assigneeDisplayName: '',
          assigneeAvatarUrl: '',
          issueTypeName: '',
          issueTypeIconUrl: '',
          browseUrl: '',
        },
      });
      this.reset();
      this.visible.set(false);
    }
  }

  onClose(): void {
    this.reset();
    this.visible.set(false);
  }

  issueTypeClass(typeName: string | null): string {
    const lower = (typeName ?? '').toLowerCase();
    const match = ISSUE_TYPE_PATTERNS.find(({ keywords }) => keywords.some((k) => lower.includes(k)));
    return match?.cls ?? 'other';
  }

  private reset(): void {
    this.selectedIssue.set(null);
    this.manualId.set('');
    this.search.set('');
    this.errorKey.set(null);
    this.trackerError.set(null);
    this.issues.set([]);
    this.providers.set([]);
    this.integration.set(null);
    this.hasLoaded = false;
  }

  private loadProviders(): void {
    this.loading.set(true);
    this.errorKey.set(null);
    this.trackerError.set(null);
    this.integrationService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (integrations) => {
          const providers = integrations.filter((i) => i.active && REQUIREMENTS_PROVIDERS.has(i.providerType));
          this.providers.set(providers);
          if (providers.length === 0) {
            this.integration.set(null);
            this.errorKey.set('requirements.issuePicker.noIntegration');
            this.loading.set(false);
            this.cdr.markForCheck();
            return;
          }
          this.integration.set(providers[0]);
          this.loadIssuesForCurrent();
        },
        error: () => {
          this.errorKey.set('requirements.issuePicker.loadError');
          this.loading.set(false);
          this.hasLoaded = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadIssuesForCurrent(): void {
    const provider = this.integration();
    if (!provider) return;
    this.loading.set(true);
    this.errorKey.set(null);
    this.trackerError.set(null);
    // Page through all issues: importable ones (e.g. Redmine „Anforderung") may sit
    // behind pages of non-importable ones and would be lost with a single-page fetch.
    this.integrationService
      .getAllIssues(this.projectId(), provider.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ issues }) => {
          this.issues.set(issues);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          const tracker = mapTrackerError(err, provider.boardUrl);
          this.trackerError.set(tracker);
          this.errorKey.set(
            tracker?.key ??
              (err.status === 412 ? 'requirements.issuePicker.tokenMissing' : 'requirements.issuePicker.loadError'),
          );
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }
}
